import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Message {
  id: string;
  nombre: string;
  email: string;
  telefono: string;
  servicio: string;
  mensaje: string;
  leido: boolean;
  created_at: string;
  imagenes?: string[];
}

type Filter = 'todos' | 'sin_leer' | 'leidos';

interface Props {
  onBadgeUpdate: (unread: number) => void;
}

const isVentaMessage = (msg: Message) => msg.servicio === 'Venta de vehículo';

const parseVentaData = (mensaje: string): Record<string, string> => {
  const lines = mensaje.split('\n').filter((l) => l.trim() && !l.startsWith('---'));
  const result: Record<string, string> = {};
  for (const line of lines) {
    const colonIdx = line.indexOf(':');
    if (colonIdx > -1) {
      const key = line.slice(0, colonIdx).trim();
      const val = line.slice(colonIdx + 1).trim();
      if (key && val) result[key] = val;
    }
  }
  return result;
};

const ImageLightbox = ({ images, onClose, startIdx = 0 }: { images: string[]; onClose: () => void; startIdx?: number }) => {
  const [current, setCurrent] = useState(startIdx);
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center" onClick={onClose}>
      <div className="relative w-full max-w-4xl px-4" onClick={(e) => e.stopPropagation()}>
        <img src={images[current]} alt={`Foto ${current + 1}`} className="w-full max-h-[80vh] object-contain mx-auto" />
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={() => setCurrent((c) => Math.max(0, c - 1))}
            disabled={current === 0}
            className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <span className="text-zinc-400 text-sm">{current + 1} / {images.length}</span>
          <button
            onClick={() => setCurrent((c) => Math.min(images.length - 1, c + 1))}
            disabled={current === images.length - 1}
            className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white cursor-pointer transition-colors">
        <i className="ri-close-line text-xl" />
      </button>
    </div>
  );
};

const VentaDetails = ({ mensaje, imagenes }: { mensaje: string; imagenes?: string[] }) => {
  const data = parseVentaData(mensaje);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const fields = ['Vehículo', 'Matrícula', 'Kilómetros', 'Combustible', 'Estado', 'Precio esperado'];
  const photos = imagenes?.filter(Boolean) ?? [];

  return (
    <div className="bg-zinc-800/50 border-l-2 border-yellow-400/40 p-3 space-y-3">
      <p className="text-yellow-400/80 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
        <i className="ri-car-line" /> Solicitud de venta de vehículo
      </p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
        {fields.map((f) => data[f] ? (
          <div key={f}>
            <p className="text-zinc-600 text-[10px] uppercase tracking-wider">{f}</p>
            <p className="text-zinc-200 text-xs font-bold">{data[f]}</p>
          </div>
        ) : null)}
      </div>
      {data['Descripción adicional'] && (
        <div className="pt-2 border-t border-zinc-700">
          <p className="text-zinc-600 text-[10px] uppercase tracking-wider mb-1">Descripción</p>
          <p className="text-zinc-300 text-xs">{data['Descripción adicional']}</p>
        </div>
      )}

      {/* Photo gallery */}
      {photos.length > 0 && (
        <div className="pt-2 border-t border-zinc-700">
          <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2 flex items-center gap-1">
            <i className="ri-image-line" /> Fotos del vehículo ({photos.length})
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {photos.map((src, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setLightboxIdx(idx)}
                className="relative aspect-square bg-zinc-900 border border-zinc-700 hover:border-yellow-400/40 overflow-hidden cursor-pointer transition-colors group"
              >
                <img src={src} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                  <i className="ri-zoom-in-line text-white text-xl" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {photos.length === 0 && (
        <div className="pt-2 border-t border-zinc-700">
          <p className="text-zinc-600 text-[10px] uppercase tracking-wider flex items-center gap-1">
            <i className="ri-image-line" /> Sin fotos adjuntas
          </p>
        </div>
      )}

      {lightboxIdx !== null && (
        <ImageLightbox images={photos} startIdx={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}
    </div>
  );
};

const MessagesTab = ({ onBadgeUpdate }: Props) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('todos');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchMessages = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('TALLERMECANICOKEVIN_contact_messages')
      .select('*')
      .order('created_at', { ascending: false });
    const msgs = data ?? [];
    setMessages(msgs);
    onBadgeUpdate(msgs.filter((m) => !m.leido).length);
    setLoading(false);
  };

  useEffect(() => { fetchMessages(); }, []);

  const toggleRead = async (msg: Message) => {
    const newVal = !msg.leido;
    await supabase.from('TALLERMECANICOKEVIN_contact_messages').update({ leido: newVal }).eq('id', msg.id);
    setMessages((prev) => {
      const updated = prev.map((m) => m.id === msg.id ? { ...m, leido: newVal } : m);
      onBadgeUpdate(updated.filter((m) => !m.leido).length);
      return updated;
    });
  };

  const deleteMsg = async (id: string) => {
    if (!confirm('¿Eliminar este mensaje?')) return;
    await supabase.from('TALLERMECANICOKEVIN_contact_messages').delete().eq('id', id);
    setMessages((prev) => {
      const updated = prev.filter((m) => m.id !== id);
      onBadgeUpdate(updated.filter((m) => !m.leido).length);
      return updated;
    });
    if (expanded === id) setExpanded(null);
  };

  const handleExpand = async (msg: Message) => {
    if (expanded === msg.id) { setExpanded(null); return; }
    setExpanded(msg.id);
    if (!msg.leido) {
      await supabase.from('TALLERMECANICOKEVIN_contact_messages').update({ leido: true }).eq('id', msg.id);
      setMessages((prev) => {
        const updated = prev.map((m) => m.id === msg.id ? { ...m, leido: true } : m);
        onBadgeUpdate(updated.filter((m) => !m.leido).length);
        return updated;
      });
    }
  };

  const filtered = messages.filter((m) => {
    if (filter === 'sin_leer' && m.leido) return false;
    if (filter === 'leidos' && !m.leido) return false;
    const q = search.toLowerCase();
    return !q || m.nombre?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q) || m.servicio?.toLowerCase().includes(q) || m.mensaje?.toLowerCase().includes(q);
  });

  const total = messages.length;
  const unread = messages.filter((m) => !m.leido).length;

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: 'Total', value: total },
          { label: 'Sin leer', value: unread, accent: true },
          { label: 'Leídos', value: total - unread },
        ].map((s) => (
          <div key={s.label} className="bg-zinc-900 border border-zinc-800 p-3 md:p-4 rounded-lg">
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">{s.label}</p>
            <p className={`font-black text-xl md:text-2xl ${s.accent ? 'text-yellow-400' : 'text-white'}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex gap-1 bg-zinc-900 border border-zinc-800 p-1 rounded-lg w-full">
          {(['todos', 'sin_leer', 'leidos'] as Filter[]).map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`flex-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap rounded-md ${filter === f ? 'bg-yellow-400 text-black' : 'text-zinc-400 hover:text-white'}`}>
              {f === 'todos' ? 'Todos' : f === 'sin_leer' ? 'Sin leer' : 'Leídos'}
            </button>
          ))}
        </div>
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..."
            className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm pl-9 pr-4 py-2.5 outline-none focus:border-yellow-400/40 transition-colors placeholder:text-zinc-600 rounded-lg" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-zinc-600">
          <i className="ri-inbox-line text-3xl mb-2" /><p className="text-sm">No hay mensajes</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((msg) => (
            <div key={msg.id} className={`border rounded-lg transition-colors ${!msg.leido ? 'border-yellow-400/20 bg-yellow-400/5' : 'border-zinc-800 bg-zinc-900'}`}>
              <div className="flex items-center gap-3 p-3 md:p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors rounded-lg" onClick={() => handleExpand(msg)}>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${!msg.leido ? 'bg-yellow-400' : 'bg-zinc-700'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <p className="text-white font-bold text-sm truncate">{msg.nombre || '—'}</p>
                    {msg.servicio && <span className="text-[10px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded whitespace-nowrap">{msg.servicio}</span>}
                    {isVentaMessage(msg) && (msg.imagenes?.length ?? 0) > 0 && (
                      <span className="text-[10px] bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 px-2 py-0.5 rounded flex items-center gap-0.5 whitespace-nowrap">
                        <i className="ri-image-line" /> {msg.imagenes!.length}
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-500 text-xs truncate">{msg.mensaje}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-zinc-600 text-[10px] hidden sm:block">{new Date(msg.created_at).toLocaleDateString('es-ES')}</span>
                  <i className={`ri-arrow-${expanded === msg.id ? 'up' : 'down'}-s-line text-zinc-500 text-base`} />
                </div>
              </div>

              {expanded === msg.id && (
                <div className="border-t border-zinc-800 p-3 md:p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-3">
                    {[
                      { label: 'Teléfono', value: msg.telefono },
                      { label: 'Email', value: msg.email },
                      { label: 'Fecha', value: new Date(msg.created_at).toLocaleString('es-ES') },
                    ].map((f) => (
                      <div key={f.label} className="bg-zinc-800/40 rounded-lg p-2.5">
                        <p className="text-zinc-600 text-[10px] uppercase tracking-wider mb-0.5">{f.label}</p>
                        <p className="text-zinc-300 text-xs font-medium break-all">{f.value || '—'}</p>
                      </div>
                    ))}
                  </div>
                  {isVentaMessage(msg) ? (
                    <VentaDetails mensaje={msg.mensaje ?? ''} imagenes={msg.imagenes} />
                  ) : (
                    <div className="bg-zinc-800/50 border-l-2 border-yellow-400/40 p-3 rounded-r-lg">
                      <p className="text-zinc-600 text-[10px] uppercase tracking-wider mb-1">Mensaje</p>
                      <p className="text-zinc-200 text-sm whitespace-pre-wrap">{msg.mensaje || '—'}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => toggleRead(msg)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap rounded-lg">
                      <i className={`${msg.leido ? 'ri-mail-unread-line' : 'ri-mail-check-line'} text-sm`} />
                      {msg.leido ? 'Sin leer' : 'Leído'}
                    </button>
                    {msg.telefono && (
                      <a href={`https://wa.me/${msg.telefono.replace(/\D/g, '')}?text=Hola%20${encodeURIComponent(msg.nombre)}%2C%20te%20contactamos%20desde%20el%20taller`}
                        target="_blank" rel="nofollow noreferrer"
                        className="flex items-center gap-1.5 px-3 py-2 bg-green-500/10 hover:bg-green-500/20 text-green-400 text-xs font-bold uppercase tracking-wider transition-colors whitespace-nowrap rounded-lg">
                        <i className="ri-whatsapp-line text-sm" /> WhatsApp
                      </a>
                    )}
                    <button onClick={() => deleteMsg(msg.id)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap rounded-lg">
                      <i className="ri-delete-bin-line text-sm" /> Eliminar
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MessagesTab;
